import { Avatar, List, Button, message, Select } from 'antd';
import { _fetch } from '../../_fetch';
import moment from 'moment';
import { Comment } from '@ant-design/compatible';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { user_role } from '../../consts';
import UserInfo from '../userInfo';
import { useGetAllUsers } from '../../utils/queryUtils';
import { trackEvent } from '../../utils/mixpanel';
import { useIsPrimaryClientView } from '../../utils/useIsPrimaryClientView';
import { ComposedInputBox, AttachedFileChip } from '../Commentshared';

const { Option } = Select;

export default function SendEmailView({
    comments = null,
    setComments = null,
    fy = null,
    selectedPrimaryClient = false,
}) {
    const [newComment, setNewComment] = useState('');
    const [toggle, setToggle] = useState(false);
    const { companyName } = useParams();
    const userData = useGetAllUsers(false, {}, companyName)?.data;
    const all_users = userData?.data;
    const { company: selected } = useSelector((st) => st.selected);
    const { role } = useSelector((st) => st.user);
    const { isReadOnly } = useIsPrimaryClientView();
    const [user, setUser] = useState([]);

    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploadingIds, setUploadingIds] = useState({});
    const uploadUrlRef = useRef({});

    const [commentFiles, setCommentFiles] = useState({});
    const filesLoadedRef = useRef(false);

    useEffect(() => {
        if (!comments?.length || filesLoadedRef.current) return;

        const fetchFiles = async () => {
            try {
                if (!fy) return;
                const res = await _fetch(`${process.env.REACT_APP_API_URL}/file?fy=${fy}`);
                if (res.status === 200) {
                    const data = await res.json();
                    const filesArr = data.response || [];
                    const grouped = {};
                    filesArr.forEach(f => {
                        if (!f.commentId) return;
                        if (!grouped[f.commentId]) grouped[f.commentId] = [];
                        grouped[f.commentId].push(f);
                    });
                    setCommentFiles(grouped);
                    filesLoadedRef.current = true;
                }
            } catch (e) {
                console.error(e);
            }
        };

        fetchFiles();
    }, [comments]);

    const handleChange = (value) => setUser([...value]);

    useEffect(() => {
        if (newComment?.length === 0) {
            setUser([]);
            setToggle(false);
        }
    }, [newComment]);

    const handleBeforeUpload = async (file) => {
        const key = `${file.name}-${Date.now()}-${Math.random()}`;
        file._key = key;

        let preview = null;
        if (file.type?.startsWith('image/')) {
            preview = URL.createObjectURL(file);
        }

        try {
            const parts = file.name.split('.');
            const ext = parts.pop();
            const baseName = parts.join('.');
            const uniqueName = `${baseName}-${moment().valueOf()}.${ext}`;

            if (!fy) return false;

            const urlRes = await _fetch(
                `${process.env.REACT_APP_API_URL}/file/get_upload_url?name=${encodeURIComponent(uniqueName)}&fy=${fy}`
            );
            if (urlRes.status === 200) {
                const signedUrl = await urlRes.json();
                uploadUrlRef.current[key] = {
                    signedUrl,
                    contentType: file.type || 'application/octet-stream',
                };
            }
        } catch (e) {
            console.error('Error pre-fetching upload URL', e);
        }

        setPendingFiles(prev => [...prev, { file, preview, key, name: file.name }]);
        return false;
    };

    const removePendingFile = (key) => {
        setPendingFiles(prev => {
            const f = prev.find(p => p.key === key);
            if (f?.preview) URL.revokeObjectURL(f.preview);
            return prev.filter(p => p.key !== key);
        });
        delete uploadUrlRef.current[key];
    };

    const clearInput = () => {
        setNewComment('');
        setUser([]);
        setToggle(false);
        pendingFiles.forEach(pf => pf.preview && URL.revokeObjectURL(pf.preview));
        setPendingFiles([]);
        uploadUrlRef.current = {};
    };

    async function handleNewComment() {
        const uploadedFiles = [];
        if (pendingFiles.length > 0) {
            for (const pf of pendingFiles) {
                const { file, key } = pf;
                const meta = uploadUrlRef.current[key];
                if (!meta) continue;
                try {
                    setUploadingIds(prev => ({ ...prev, [key]: 10 }));
                    const s3Res = await fetch(meta.signedUrl, {
                        method: 'PUT',
                        headers: { 'Content-Type': meta.contentType },
                        body: file,
                    });
                    setUploadingIds(prev => ({ ...prev, [key]: 70 }));
                    if (s3Res.status === 200) {
                        const parsedUrl = new URL(meta.signedUrl);
                        const pathname = parsedUrl.pathname.slice(1);
                        uploadedFiles.push({ key, pathname, contentType: meta.contentType, name: file.name });
                    }
                    setUploadingIds(prev => ({ ...prev, [key]: 100 }));
                } catch (e) {
                    console.error('S3 upload error', e);
                    message.error(`Failed to upload ${file.name}`);
                }
            }
        }

        const payload = { fy, text: newComment, arr: user };
        if (selectedPrimaryClient?._id) {
            payload.arr = [selectedPrimaryClient._id];
            payload.isPrimaryClientComments = true;
        }

        try {
            let res = await _fetch(`${process.env.REACT_APP_API_URL}/comment`, {
                method: 'POST',
                body: { ...payload },
            });

            if (res.status === 201) {
                let result = await res.json();
                const commentId = result._id;

                for (const uf of uploadedFiles) {
                    const fileBody = { path: uf.pathname, type: uf.contentType, commentId, fy };
                    const fileRes = await _fetch(`${process.env.REACT_APP_API_URL}/file`, {
                        method: 'POST',
                        body: fileBody,
                    });
                    if (fileRes.status === 201) {
                        const savedFile = await fileRes.json();
                        setCommentFiles(prev => ({
                            ...prev,
                            [commentId]: [...(prev[commentId] || []), savedFile],
                        }));
                    }
                }

                if (fy) {
                    await _fetch(`${process.env.REACT_APP_API_URL}/comment/${commentId}/send-email`, {
                        method: 'POST',
                    });
                }

                message.success('Email sent successfully');
                setNewComment('');
                setComments(c => [result, ...c]);
                setUser([]);
                setToggle(false);
                setPendingFiles([]);
                uploadUrlRef.current = {};
                setUploadingIds({});

                trackEvent('Add Comment', { comment_id: commentId, text: newComment, fy });
                return commentId;
            }
        } catch (e) {
            console.error(e);
        }
    }

    const hasContent = newComment.trim().length > 0 || pendingFiles.length > 0;
    const isUploading = Object.keys(uploadingIds).length > 0;

    return (
        <React.Fragment>
            <div>
                <ComposedInputBox
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    pendingFiles={pendingFiles}
                    uploadingIds={uploadingIds}
                    onRemoveFile={removePendingFile}
                    onBeforeUpload={handleBeforeUpload}
                    placeholder="Type your email message..."
                />

                {hasContent && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        marginTop: 10,
                        flexWrap: 'wrap',
                        gap: 8,
                    }}>
                        <Button onClick={clearInput}>Cancel</Button>

                        {toggle && role !== user_role.PRIMARY_CLIENT && !selectedPrimaryClient?.id && (
                            <Select
                                mode="multiple"
                                showSearch
                                style={{ minWidth: 300 }}
                                placeholder="Select recipients"
                                defaultValue={user}
                                onChange={handleChange}
                                optionLabelProp="label"
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
                                }
                            >
                                {all_users
                                    ?.filter(e =>
                                        e.role === user_role.ADMIN ||
                                        e.company_ids?.some(f => f._id === selected._id)
                                    )
                                    .map(elem => (
                                        <Option
                                            key={elem._id}
                                            label={`${elem.first_name} ${elem.last_name}`}
                                            value={elem._id}
                                        >
                                            <UserInfo
                                                firstName={elem.first_name}
                                                lastName={elem.last_name}
                                                email={elem.email}
                                            />
                                        </Option>
                                    ))}
                            </Select>
                        )}

                        <Button
                            type="primary"
                            loading={isUploading}
                            disabled={(!selectedPrimaryClient?._id && toggle && !user?.length) || isReadOnly}
                            onClick={async () => {
                                if (selectedPrimaryClient?._id) {
                                    let commentId = await handleNewComment();
                                    if (commentId) window.myFileUpload?.(commentId);
                                    return;
                                }
                                if (!toggle && role === user_role.PRIMARY_CLIENT) {
                                    let commentId = await handleNewComment();
                                    if (commentId) window.myFileUpload?.(commentId);
                                } else if (!toggle) {
                                    setToggle(true);
                                } else {
                                    let commentId = await handleNewComment();
                                    if (commentId) window.myFileUpload?.(commentId);
                                }
                            }}
                        >
                            Send Email
                        </Button>
                    </div>
                )}
            </div>

            {comments ? (
                <List
                    className="comment-list w-100 px-3"
                    itemLayout="horizontal"
                    dataSource={comments}
                    pagination={{ pageSize: 10 }}
                    style={{ marginTop: 16 }}
                    renderItem={(item) => {
                        const filesForComment = commentFiles[item._id] || [];

                        return (
                            <Comment
                                style={{
                                    margin: '9px 0',
                                    backgroundColor: '#FAFAFA',
                                    paddingLeft: 10,
                                    borderRadius: 8,
                                    border: '1px solid #f0f0f0',
                                }}
                                author={
                                    <div>
                                        <b>{item?.created_by?.first_name} {item?.created_by?.last_name}</b>
                                        <span className="ms-2" style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 12 }}>
                                            {moment(item?.createdAt).format('DD MMM YYYY HH:mm')}
                                        </span>
                                    </div>
                                }
                                avatar={
                                    <Avatar shape="square" style={{ background: '#1677ff' }}>
                                        {item?.created_by?.first_name?.[0]}
                                        {item?.created_by?.last_name?.[0]}
                                    </Avatar>
                                }
                                content={
                                    <div style={{ backgroundColor: '#FAFAFA' }}>
                                        <span style={{ whiteSpace: 'pre-line', fontSize: 12, fontWeight: 400 }}>
                                            {item?.text}
                                        </span>

                                        {role !== user_role.PRIMARY_CLIENT && item?.created_for?.length > 0 && (
                                            <p style={{ marginTop: 6, marginBottom: 4 }}>
                                                <span style={{ color: '#6FBAFF', fontWeight: 600, fontSize: 12 }}>Recipients: </span>
                                                <span style={{ color: '#00000073', fontWeight: 700, fontSize: 12 }}>
                                                    {item.created_for.map(el => `${el?.first_name} ${el?.last_name}`).join(', ')}
                                                </span>
                                            </p>
                                        )}

                                        {filesForComment.length > 0 && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                                                {filesForComment.map((f, i) => (
                                                    <AttachedFileChip key={f._id || i} file={f} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        );
                    }}
                />
            ) : null}
        </React.Fragment>
    );
}